import * as React from "react";

import { useHttpClient } from "@/providers/HttpClient";
import { Button } from "@/shared/foundation/primitives";
import { robotoHeaders } from "@/shared/http";
import { LoggerService } from "@/shared/services/LoggerService";

import styles from "./ReExtractButton.module.css";

interface ReExtractButtonProps {
  orgId: string;
  threadId: string;
}

/**
 * Forces a fresh knowledge-extraction pass over one thread.
 *
 * Every automatic path refuses to revisit a thread: extraction stamps a marker on success —
 * including a success that wrote nothing — and re-eligibility then requires new messages. So a
 * thread processed by a prompt, model or toolset that has since been fixed can never benefit from
 * the fix. This button is the way to act on such a fix without touching the database.
 *
 * Deliberately fire-and-forget. The pass is an LLM tool-use loop that routinely runs 30–90 seconds,
 * far longer than a request, so the endpoint only enqueues it — there is no result to await and
 * polling for one would imply a completion signal the API does not offer. The button reports
 * whether the work was *queued*, and points at where the outcome actually shows up.
 */
export function ReExtractButton({ orgId, threadId }: ReExtractButtonProps) {
  const { httpClient } = useHttpClient();
  const [isEnqueuing, setIsEnqueuing] = React.useState(false);
  const [status, setStatus] = React.useState<"queued" | "error" | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleReExtract = async () => {
    setIsEnqueuing(true);
    setStatus(null);
    setErrorMessage(null);

    try {
      const url = httpClient.constructUrl(
        `v1/knowledge-graph/orgs/${orgId}/threads/${threadId}/extract`,
      );
      await httpClient.post(url, {
        body: JSON.stringify({}),
        headers: robotoHeaders({ orgId }),
      });
      setStatus("queued");
    } catch (err) {
      LoggerService.error("Failed to trigger thread re-extraction", err);
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to queue re-extraction",
      );
    } finally {
      setIsEnqueuing(false);
    }
  };

  return (
    <div className={styles.container}>
      <Button
        size="small"
        variant="outlined"
        disabled={isEnqueuing}
        onClick={() => void handleReExtract()}
      >
        {isEnqueuing ? "Queueing…" : "Re-extract knowledge"}
      </Button>
      {status === "queued" && (
        <span className={styles.queued}>
          Queued — runs in the background, takes 30–90s. Reload to see new
          traces.
        </span>
      )}
      {status === "error" && errorMessage && (
        <span className={styles.error}>{errorMessage}</span>
      )}
    </div>
  );
}
